/**
 * Client-side navigation.
 *
 * Shopify serves storefront HTML with `cache-control: no-store`, which makes
 * every page ineligible for the browser's back/forward cache. Going Back is
 * therefore always a full reload: the page visibly flashes, and clicks are
 * dropped while it happens. A theme cannot change response headers, so the only
 * way to remove that reload is to stop handing navigation to the browser.
 *
 * What this does:
 *   - intercepts same-origin link clicks and swaps `#MainContent` instead of
 *     performing a document navigation;
 *   - keeps a snapshot of every page visited, so Back/Forward restore instantly
 *     from memory with no network request and no dead click window;
 *   - warms the cache on hover, so forward navigation is usually instant too.
 *
 * Header, footer and <cart-drawer> live outside #MainContent and are never
 * replaced, so cart state survives navigation untouched.
 *
 * Safety: cart, checkout and account routes are never intercepted, and any
 * error at all falls back to a normal browser navigation. If this script fails
 * to load or throws, the site behaves exactly as it did before.
 */
(function () {
  'use strict';

  var MAIN = '#MainContent';
  var CACHE_LIMIT = 30;
  var HOVER_DELAY = 60;

  // A collection page shows 24 product cards. Without a budget, scanning down
  // one with the mouse would prefetch two dozen full product documents — real
  // bandwidth on a phone, and worthless if the shopper is only browsing. Six
  // per page keeps navigation feeling instant without downloading the catalogue.
  var PREFETCH_BUDGET = 6;
  // Retained page HTML is held as JS strings; on a large catalogue a few
  // collection pages add up, so the cache is bounded by size as well as count.
  var CACHE_BYTES_MAX = 4 * 1024 * 1024;

  // Session-dependent routes must always be a real navigation. Checkout in
  // particular must never be fetched or swapped.
  var SKIP = /\/(cart|checkout|account|challenge|password|orders|tools|services)(\/|$)/;

  // The theme editor re-renders sections through its own channel; swapping
  // documents underneath it breaks that contract.
  if (window.Shopify && window.Shopify.designMode) return;
  if (!('pushState' in history) || typeof DOMParser === 'undefined') return;

  var main = document.querySelector(MAIN);
  if (!main) return;

  var cache = new Map();
  var order = [];
  // On popstate the browser has already moved window.location to the incoming
  // URL, so the outgoing page cannot be identified from it. Tracking the key
  // separately is what makes snapshotting the page we are leaving correct.
  var currentKey = null;
  var pending = null;
  var hoverTimer = null;
  var navToken = 0;
  var cacheBytes = 0;
  var prefetchesThisPage = 0;
  var prefetchInFlight = false;

  /* ---------------------------------------------------------------- utils */

  function sameOrigin(url) {
    return url.origin === window.location.origin;
  }

  function key(url) {
    return url.pathname + url.search;
  }

  function remember(k, entry) {
    var existing = cache.get(k);
    if (existing) {
      cacheBytes -= existing.bytes || 0;
      order.splice(order.indexOf(k), 1);
    }
    entry.bytes = (entry.html || '').length * 2; // JS strings are UTF-16
    cache.set(k, entry);
    order.push(k);
    cacheBytes += entry.bytes;

    while (order.length && (order.length > CACHE_LIMIT || cacheBytes > CACHE_BYTES_MAX)) {
      var oldest = order.shift();
      if (oldest === k) { order.push(oldest); break; } // never evict what we just stored
      var dropped = cache.get(oldest);
      if (dropped) cacheBytes -= dropped.bytes || 0;
      cache.delete(oldest);
    }
  }

  function eligible(anchor) {
    if (!anchor || !anchor.href) return false;
    var url;
    try { url = new URL(anchor.href); } catch (e) { return false; }
    if (!sameOrigin(url)) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.hasAttribute('data-no-instant')) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (SKIP.test(url.pathname)) return false;
    // In-page anchors must keep native behaviour.
    if (url.pathname === window.location.pathname && url.hash) return false;
    return true;
  }

  /* --------------------------------------------------------- asset loading */

  function assetName(value) {
    return (value || '').split('?')[0];
  }

  // Dawn loads CSS and JS per section, so a page we have never rendered may
  // need assets the current document has not loaded. Stylesheets are awaited so
  // content is never swapped in unstyled; scripts execute on append, and any
  // custom elements they define upgrade the already-inserted markup.
  function loadStyles(doc) {
    var have = {};
    var links = document.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < links.length; i++) have[assetName(links[i].getAttribute('href'))] = true;

    var waits = [];
    var incoming = doc.querySelectorAll('link[rel="stylesheet"]');
    for (var j = 0; j < incoming.length; j++) {
      var href = incoming[j].getAttribute('href');
      if (!href || have[assetName(href)]) continue;
      have[assetName(href)] = true;
      (function (h) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = h;
        waits.push(new Promise(function (resolve) {
          link.onload = resolve;
          link.onerror = resolve;
          // Never let a hanging stylesheet block navigation.
          setTimeout(resolve, 1500);
        }));
        document.head.appendChild(link);
      })(href);
    }
    return Promise.all(waits);
  }

  // Awaited before the swap. A section's behaviour lives in its script, and
  // Dawn's custom elements only upgrade once `customElements.define` has run —
  // swapping first would briefly expose an un-upgraded <product-form>, whose
  // native submit posts to /cart and navigates away instead of opening the
  // cart drawer.
  function loadScripts(doc) {
    var have = {};
    var existing = document.querySelectorAll('script[src]');
    for (var i = 0; i < existing.length; i++) have[assetName(existing[i].getAttribute('src'))] = true;

    var waits = [];
    var incoming = doc.querySelectorAll('script[src]');
    for (var j = 0; j < incoming.length; j++) {
      var src = incoming[j].getAttribute('src');
      if (!src || have[assetName(src)]) continue;
      have[assetName(src)] = true;
      (function (source) {
        var s = document.createElement('script');
        s.src = source;
        waits.push(new Promise(function (resolve) {
          s.onload = resolve;
          s.onerror = resolve;
          setTimeout(resolve, 3000);
        }));
        document.head.appendChild(s);
      })(src);
    }
    return Promise.all(waits);
  }

  // innerHTML never executes scripts. Section markup can carry inline scripts
  // (product JSON payloads, for example), so they are re-created to run.
  function runInlineScripts(root) {
    var scripts = root.querySelectorAll('script:not([src])');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var s = document.createElement('script');
      if (old.type) s.type = old.type;
      s.textContent = old.textContent;
      old.parentNode.replaceChild(s, old);
    }
  }

  /* ------------------------------------------------------------- metadata */

  // Shopify ships per-page analytics metadata (page type, resource id, product
  // payload) inside content_for_header, which a content swap never touches.
  // Left stale, every event would be attributed to the page the visitor first
  // landed on. The JSON is extracted and merged rather than re-executing
  // Shopify's script, so none of its initialisation runs twice.
  var META_RE = /var meta = (\{[\s\S]*?\});\s*for \(var attr in meta\)/;

  function extractAnalyticsMeta(doc) {
    try {
      var scripts = doc.querySelectorAll('script:not([src])');
      for (var i = 0; i < scripts.length; i++) {
        var text = scripts[i].textContent || '';
        if (text.indexOf('ShopifyAnalytics.meta') === -1) continue;
        var match = text.match(META_RE);
        if (match) return JSON.parse(match[1]);
      }
    } catch (e) { /* analytics must never break navigation */ }
    return null;
  }

  function applyAnalytics(meta) {
    try {
      if (meta && window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
        for (var attr in meta) {
          if (Object.prototype.hasOwnProperty.call(meta, attr)) {
            window.ShopifyAnalytics.meta[attr] = meta[attr];
          }
        }
      }
    } catch (e) { /* ignore */ }

    // Web Pixels (custom pixels, Meta/Google integrations).
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('page_viewed', {});
      }
    } catch (e) { /* ignore */ }

    // Shopify's own admin reporting.
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.page === 'function') {
        window.ShopifyAnalytics.lib.page(null, {
          path: window.location.pathname,
          url: window.location.href,
          title: document.title
        });
      }
    } catch (e) { /* ignore */ }
  }

  function headContent(doc, selector, attr) {
    var el = (doc || document).querySelector(selector);
    return el ? el.getAttribute(attr) : null;
  }

  function setHeadMeta(entry) {
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && entry.canonical) canonical.setAttribute('href', entry.canonical);
    var description = document.querySelector('meta[name="description"]');
    if (description && entry.description !== null && entry.description !== undefined) {
      description.setAttribute('content', entry.description);
    }
  }

  /* ------------------------------------------------------------ overlays */

  // The header menus, mobile drawer and cart drawer all live outside
  // #MainContent, so a content swap leaves them exactly as the visitor left
  // them. A real navigation resets them; without this a shopper who taps a
  // category in the mobile menu gets the new page rendered behind a menu that
  // is still open over it.
  function closeOverlays() {
    try {
      var open = document.querySelectorAll('details[open]');
      for (var i = 0; i < open.length; i++) {
        if (main.contains(open[i])) continue;
        open[i].removeAttribute('open');
        var summary = open[i].querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }

      var drawer = document.querySelector('cart-drawer.active');
      if (drawer) drawer.classList.remove('active');

      // Dawn locks page scroll while an overlay is open.
      document.body.classList.remove('overflow-hidden');
    } catch (e) { /* never block navigation */ }
  }

  /* -------------------------------------------------------- accessibility */

  var liveRegion = null;

  function announce(title) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }
    // Cleared first so repeat navigations to the same title still announce.
    liveRegion.textContent = '';
    window.setTimeout(function () { liveRegion.textContent = title; }, 60);
  }

  /* -------------------------------------------------------------- snapshot */

  function snapshot() {
    var el = document.querySelector(MAIN);
    if (!el || !currentKey) return;
    remember(currentKey, {
      html: el.innerHTML,
      className: el.className,
      template: el.getAttribute('data-template'),
      title: document.title,
      canonical: headContent(document, 'link[rel="canonical"]', 'href'),
      description: headContent(document, 'meta[name="description"]', 'content'),
      analyticsMeta: (function () {
        try {
          return window.ShopifyAnalytics && window.ShopifyAnalytics.meta
            ? JSON.parse(JSON.stringify(window.ShopifyAnalytics.meta))
            : null;
        } catch (e) { return null; }
      })(),
      scrollY: window.scrollY
    });
  }

  function applyEntry(entry, scrollY) {
    var el = document.querySelector(MAIN);
    if (!el) return false;
    el.innerHTML = entry.html;
    if (entry.className) el.className = entry.className;
    if (entry.template) el.setAttribute('data-template', entry.template);
    document.title = entry.title;
    currentKey = key(new URL(window.location.href));
    prefetchesThisPage = 0;
    closeOverlays();
    setHeadMeta(entry);
    runInlineScripts(el);
    window.scrollTo(0, typeof scrollY === 'number' ? scrollY : 0);

    // A content swap leaves focus wherever the visitor clicked, which for a
    // keyboard or screen-reader user is now detached from what is on screen.
    // <main> already carries tabindex="-1" for exactly this purpose.
    try { el.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    announce(entry.title);

    applyAnalytics(entry.analyticsMeta);

    document.dispatchEvent(new CustomEvent('bebeomi:navigated', {
      detail: { url: window.location.href }
    }));
    return true;
  }

  /* ------------------------------------------------------------- fetching */

  function fetchPage(url) {
    var k = key(url);
    var hit = cache.get(k);
    if (hit && hit.html) return Promise.resolve(hit);

    return fetch(url.href, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        // A redirect off-origin or into checkout must become a real navigation.
        if (!sameOrigin(new URL(res.url))) throw new Error('cross-origin redirect');
        return res.text().then(function (text) { return { text: text, finalUrl: res.url }; });
      })
      .then(function (r) {
        var doc = new DOMParser().parseFromString(r.text, 'text/html');
        var incomingMain = doc.querySelector(MAIN);
        if (!incomingMain) throw new Error('no main content');
        var entry = {
          html: incomingMain.innerHTML,
          className: incomingMain.className,
          template: incomingMain.getAttribute('data-template'),
          title: doc.title,
          canonical: headContent(doc, 'link[rel="canonical"]', 'href'),
          description: headContent(doc, 'meta[name="description"]', 'content'),
          analyticsMeta: extractAnalyticsMeta(doc),
          doc: doc,
          finalUrl: r.finalUrl
        };
        remember(key(new URL(r.finalUrl)), entry);
        return entry;
      });
  }

  function hardNavigate(url) {
    window.location.href = url.href;
  }

  /* ----------------------------------------------------------- navigation */

  function navigate(url, opts) {
    opts = opts || {};
    var token = ++navToken;

    snapshot();

    var k = key(url);
    var cached = cache.get(k);

    // Back/Forward, or a page already visited: restore from memory with no
    // network request at all. This is the whole point of the script.
    if (cached && cached.html && !cached.doc) {
      if (opts.push) history.pushState({ bebeomi: true }, '', url.href);
      applyEntry(cached, opts.scrollY);
      return;
    }

    if (pending) pending.cancelled = true;
    var job = { cancelled: false };
    pending = job;

    document.documentElement.classList.add('bebeomi-navigating');

    fetchPage(url)
      .then(function (entry) {
        if (job.cancelled || token !== navToken) return;
        return Promise.all([loadStyles(entry.doc), loadScripts(entry.doc)]).then(function () {
          if (job.cancelled || token !== navToken) return;
          if (opts.push) history.pushState({ bebeomi: true }, '', entry.finalUrl || url.href);
          applyEntry(entry, opts.scrollY);
          // The parsed document is only needed for asset discovery; dropping it
          // keeps the cache small and marks the entry as "already rendered".
          delete entry.doc;
        });
      })
      .catch(function () {
        if (job.cancelled || token !== navToken) return;
        hardNavigate(url);
      })
      .then(function () {
        if (token === navToken) document.documentElement.classList.remove('bebeomi-navigating');
      });
  }

  /* -------------------------------------------------------------- wiring */

  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  history.replaceState({ bebeomi: true }, '', window.location.href);
  currentKey = key(new URL(window.location.href));

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var anchor = event.target.closest ? event.target.closest('a[href]') : null;
    if (!eligible(anchor)) return;

    var url = new URL(anchor.href);
    if (key(url) === key(new URL(window.location.href)) && !url.hash) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    navigate(url, { push: true, scrollY: 0 });
  });

  window.addEventListener('popstate', function (event) {
    if (!event.state || !event.state.bebeomi) return;
    var url = new URL(window.location.href);
    if (SKIP.test(url.pathname)) { hardNavigate(url); return; }
    var cached = cache.get(key(url));
    navigate(url, { push: false, scrollY: cached ? cached.scrollY : 0 });
  });

  // Warm the cache on hover so forward navigation is instant as well.
  function warm(anchor) {
    if (!eligible(anchor)) return;
    if (prefetchInFlight) return;
    if (prefetchesThisPage >= PREFETCH_BUDGET) return;
    var url = new URL(anchor.href);
    if (cache.has(key(url))) return;
    prefetchesThisPage++;
    prefetchInFlight = true;
    fetchPage(url)
      .catch(function () {})
      .then(function () { prefetchInFlight = false; });
  }

  var connection = navigator.connection;
  var frugal = connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''));

  if (!frugal) {
    document.addEventListener('mouseover', function (event) {
      var anchor = event.target.closest ? event.target.closest('a[href]') : null;
      if (!anchor) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { warm(anchor); }, HOVER_DELAY);
    }, { capture: true, passive: true });

    document.addEventListener('mouseout', function () { clearTimeout(hoverTimer); }, { capture: true, passive: true });

    document.addEventListener('touchstart', function (event) {
      var anchor = event.target.closest ? event.target.closest('a[href]') : null;
      if (anchor) warm(anchor);
    }, { capture: true, passive: true });
  }
})();
